import axiosInstance from 'services/api/config/api';
import type {
    GetAccessManagerDataResponse,
    CreateTenantRequest,
    Tenant,
    CreateUserRequest,
    InviteUserRequest,
    UpdateUserRequest,
    User,
    DeleteResponse,
    ActiveAccount,
    OutboundTrunkInfo,
    WorkflowSummary,
    MaskedTenantConfigResponse,
} from 'types/accessManager.type';

export const getAccessManagerData = (): Promise<GetAccessManagerDataResponse> => {
    return axiosInstance.get<GetAccessManagerDataResponse>('/access-manager').then((res) => res.data);
};

export const createTenant = (data: CreateTenantRequest): Promise<Tenant> => {
    return axiosInstance.post<Tenant>('/access-manager/tenant', data).then((res) => res.data);
};

export const getTenant = (id: string) => {
    return axiosInstance.get<Tenant['users']>(`/access-manager/tenant/${id}`).then((res) => res.data);
};

export const deleteTenant = (id: string): Promise<DeleteResponse> => {
    return axiosInstance.delete<DeleteResponse>(`/access-manager/tenant/${id}`).then((res) => res.data);
};

export const updateTenantStatus = (id: string): Promise<Tenant> => {
    return axiosInstance.put<Tenant>(`/access-manager/tenant/status/${id}`).then((res) => res.data);
};

export const createUser = (data: CreateUserRequest): Promise<User> => {
    return axiosInstance.post<User>('/access-manager/user', data).then((res) => res.data);
};

export const inviteUser = (data: InviteUserRequest): Promise<User> => {
    return axiosInstance.post<User>('/access-manager/user/invite', data).then((res) => res.data);
};

export const updateUser = (id: string, data: UpdateUserRequest): Promise<User> => {
    return axiosInstance.put<User>(`/access-manager/user/${id}`, data).then((res) => res.data);
};

export const deleteUser = (id: string): Promise<DeleteResponse> => {
    return axiosInstance.delete<DeleteResponse>(`/access-manager/user/${id}`).then((res) => res.data);
};

export const getActiveAccounts = (): Promise<ActiveAccount[]> => {
    return axiosInstance.get<ActiveAccount[]>('/access-manager/twilio/active-accounts').then((res) => res.data);
};

export const getOutboundTrunks = (): Promise<OutboundTrunkInfo[]> => {
    return axiosInstance.get<OutboundTrunkInfo[]>('/access-manager/livekit/outbound-trunks').then((res) => res.data);
};

export const getTenantWorkflows = (): Promise<WorkflowSummary[]> => {
    return axiosInstance
        .get<{ workflows: WorkflowSummary[] }>('/access-manager/workflows')
        .then((res) => res.data.workflows);
};

export const getUserWorkflowAssignments = (id: string): Promise<WorkflowSummary[]> => {
    return axiosInstance
        .get<{ workflow_assignments: WorkflowSummary[] }>(`/access-manager/users/${id}/workflow-assignments`)
        .then((res) => res.data.workflow_assignments);
};

export const updateUserWorkflowAssignments = (id: string, workflow_ids: string[]): Promise<WorkflowSummary[]> => {
    return axiosInstance
        .put<{ workflow_assignments: WorkflowSummary[] }>(`/access-manager/users/${id}/workflow-assignments`, {
            workflow_ids,
        })
        .then((res) => res.data.workflow_assignments);
};

export const getTenantConfig = (id: string): Promise<MaskedTenantConfigResponse> => {
    return axiosInstance.get<MaskedTenantConfigResponse>(`/access-manager/tenant/${id}/config`).then((res) => res.data);
};

const accessManagerService = {
    getAccessManagerData,
    createUser,
    inviteUser,
    updateUser,
    deleteUser,
    createTenant,
    getTenant,
    deleteTenant,
    updateTenantStatus,
    getActiveAccounts,
    getOutboundTrunks,
    getTenantWorkflows,
    getUserWorkflowAssignments,
    updateUserWorkflowAssignments,
    getTenantConfig,
};

export default accessManagerService;
